/**
 * Dialog for lending an item to a group member.
 *
 * Extracted from GroupView.tsx to reduce component complexity.
 * Handles borrower selection and optional loan duration (days/weeks).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useCreateLoan } from '@/hooks/useLoans';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';
import type { ItemWithOwner } from '@/hooks/useItems';

interface GroupMember {
  user_id: string;
  display_name: string | null;
}

interface LendItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ItemWithOwner | null;
  members: GroupMember[];
  currentUserId: string;
  groupId?: string;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.item
 * @param root0.members
 * @param root0.currentUserId
 * @param root0.groupId
 */
export function LendItemDialog({
  open,
  onOpenChange,
  item,
  members,
  currentUserId,
  groupId,
}: LendItemDialogProps) {
  const createLoan = useCreateLoan();
  const { isOnline } = useNetworkStatusContext();
  const [selectedBorrower, setSelectedBorrower] = useState('');
  const [loanDuration, setLoanDuration] = useState('');
  const [loanDurationUnit, setLoanDurationUnit] = useState<'days' | 'weeks'>('days');
  const [hasDuration, setHasDuration] = useState(false);

  const handleLend = async () => {
    if (!item || !selectedBorrower) return;
    const durationNum = hasDuration && loanDuration ? parseInt(loanDuration, 10) : undefined;
    const durationDays = durationNum
      ? loanDurationUnit === 'weeks'
        ? durationNum * 7
        : durationNum
      : undefined;
    try {
      await createLoan.mutateAsync({
        itemId: item.id,
        borrowerId: selectedBorrower,
        durationDays,
        groupId,
      });
      toast.success('Loan proposal sent — waiting for acceptance');
      onOpenChange(false);
      resetForm();
    } catch {
      // Error toast handled by useCreateLoan onError
    }
  };

  const resetForm = () => {
    setSelectedBorrower('');
    setLoanDuration('');
    setLoanDurationUnit('days');
    setHasDuration(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) resetForm();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Propose Loan</DialogTitle>
          <DialogDescription>
            Choose a member to propose lending "{item?.name}" to. They will need to accept before
            the loan becomes active.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Borrower</Label>
            <Select value={selectedBorrower} onValueChange={setSelectedBorrower}>
              <SelectTrigger>
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {members
                  ?.filter((m) => m.user_id !== currentUserId)
                  .map((member) => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.display_name || 'Unknown'}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            <Label>Set a loan duration?</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="hasDuration"
                  checked={!hasDuration}
                  onChange={() => {
                    setHasDuration(false);
                    setLoanDuration('');
                  }}
                  className="accent-primary"
                />
                <span className="text-sm">No — Unlimited</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="hasDuration"
                  checked={hasDuration}
                  onChange={() => setHasDuration(true)}
                  className="accent-primary"
                />
                <span className="text-sm">Yes — Set deadline</span>
              </label>
            </div>
            {hasDuration && (
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 7"
                  value={loanDuration}
                  onChange={(e) => setLoanDuration(e.target.value)}
                  className="flex-1"
                />
                <Select
                  value={loanDurationUnit}
                  onValueChange={(v) => setLoanDurationUnit(v as 'days' | 'weeks')}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="weeks">Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button
            onClick={handleLend}
            disabled={!selectedBorrower || createLoan.isPending || !isOnline}
            title={!isOnline ? "You're offline" : undefined}
            className="w-full"
          >
            {createLoan.isPending ? 'Sending...' : 'Send Loan Proposal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
